<template>
  <div>
    <div class="row items-center">
      <div class="q-mr-md">
        <q-img
          data-private
          fit="cover"
          :src="propertyThumbnail(property)"
          style="width: 165px"
          alt="Building Image"
          class="rounded-borders"
          crossorigin="anonymous"
          @error="$emit('image-error')"
        />
      </div>

      <div class="col">
        <div class="row q-col-gutter-sm">
          <div class="col">
            <div class="row items-center">
              <div
                class="q-mb-xs ellipsis"
                style="font-size: 1rem; font-weight: 500; max-width: 300px"
              >
                {{ displayTitle }}
              </div>
              <new-property-report-icon
                v-if="canCreatePropertyReports && !isCandidate"
                :property-id="property.id"
                class="q-ml-xs"
              />
            </div>
            <div class="ellipsis" style="max-width: 300px; font-size: 0.8rem">
              {{ displayAddress }}
            </div>
          </div>
        </div>

        <div class="row q-col-gutter-xs q-col-gutter-y-sm" style="margin-top: 1px">
          <div v-if="displaySimilarity !== null" class="col-4">
            <div class="metric-label">Similarity</div>
            <q-icon name="data_usage" />
            {{ formatPercent(displaySimilarity, 0) }}
          </div>
          <div v-if="displayDistance !== null" class="col-4">
            <div class="metric-label">Distance</div>
            <q-icon name="place" />
            {{ safeMiles(displayDistance) }} mi
          </div>
          <div class="col-4">
            <div class="metric-label"># Units</div>
            <q-icon name="apartment" />
            {{ property.number_units || '-' }}
          </div>
          <div class="col-4">
            <div class="metric-label">Year Built</div>
            <q-icon name="date_range" />
            {{ property.year_built || '-' }}
          </div>
          <div v-if="displayAssetClass !== null" class="col-4">
            <div class="metric-label">Asset Class</div>
            <q-icon name="grade" />
            {{ displayAssetClass }}
          </div>
          <div v-if="deleteEnabled && !isCandidate" class="col-4">
            <q-btn
              no-caps
              icon="delete_outline"
              class="full-width"
              unelevated
              color="red"
              size="sm"
              outline
              @click.stop="$emit('delete')"
            >
              Delete
            </q-btn>
          </div>
          <div v-if="isCandidate" class="col">
            <q-btn
              no-caps
              class="full-width"
              unelevated
              color="primary"
              size="sm"
              :disable="disableAdd"
              @click.stop="$emit('add')"
            >
              Add to Comps
            </q-btn>
          </div>
        </div>
      </div>

      <!-- Separator and Similarity Breakdown - only on larger screens -->
      <template v-if="shouldShowSimilarityBreakdown">
        <q-separator vertical class="q-mx-sm" />
        <div class="similarity-breakdown col-1">
          <div class="similarity-dimensions">
            <div
              v-for="dimension in displaySimilarityDimensions"
              :key="dimension.key"
              class="similarity-dimension"
            >
              <div class="dimension-bar-container">
                <div
                  class="dimension-bar"
                  :style="{
                    width: `${(dimension.value || 0) * 100}%`,
                    backgroundColor: getSimilarityBarColor(dimension.value)
                  }"
                >
                  <q-tooltip>
                    {{ dimension.name }}: {{ formatPercent(dimension.value, 0) }}
                  </q-tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Close button positioned at far right -->
      <div class="close-button-container">
        <q-icon
          name="close"
          class="cursor-pointer font-bold"
          size="17px"
          @click.stop="$emit('close')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import {
  type PropertyDetailsResponse,
  type PropertyComparableResponse,
  type Similarities,
  humanizeQualityScore,
  getPropertyImageUrl
} from 'common'
import { formatPercent } from '@/utils'
import { useUserStore } from '@/stores/user'
import NewPropertyReportIcon from '@/components/NewPropertyReportIcon.vue'

type PropertyType = PropertyDetailsResponse | PropertyComparableResponse

export default defineComponent({
  name: 'MapPropertyCard',
  components: { NewPropertyReportIcon },
  props: {
    property: {
      type: Object as PropType<PropertyType>,
      required: true
    },
    similarities: { type: Object as PropType<Similarities | null>, default: null },
    deleteEnabled: { type: Boolean, default: false },
    isCandidate: { type: Boolean, default: false },
    disableAdd: { type: Boolean, default: false },
    canCreatePropertyReports: { type: Boolean, default: false }
  },
  emits: ['close', 'add', 'delete', 'image-error'],
  computed: {
    displayTitle(): string {
      return this.property.building_name || this.property.street_address
    },
    displayAddress(): string {
      return `${this.property.street_address}, ${this.property.city}, ${this.property.state}, ${this.property.zip_code}`
    },
    displaySimilarity(): number | null {
      if ('similarity_score' in this.property && this.property.similarity_score?.overall) {
        return this.property.similarity_score.overall ?? null
      }
      return this.similarities?.overall ?? null
    },
    displayDistance(): number | null {
      if ('distance_miles' in this.property && this.property.distance_miles) {
        return this.property.distance_miles
      }
      return this.similarities?.distance_miles ?? null
    },
    displayNumberStories(): number | null {
      return this.property.number_stories ?? null
    },
    displayAssetClass(): string | null {
      // Only PropertyDetailsResponse has building_quality
      if (
        'building_quality' in this.property &&
        this.property.building_quality?.property_overall_quality
      ) {
        return humanizeQualityScore(this.property.building_quality.property_overall_quality)
      }
      return null
    },
    shouldShowSimilarityBreakdown(): boolean {
      return this.$q.screen.gt.md
    },
    activeSimilarities(): Similarities | null {
      if ('similarity_score' in this.property && this.property.similarity_score) {
        return this.property.similarity_score
      }
      return this.similarities
    },
    displaySimilarityDimensions(): Array<{ key: string; name: string; value: number | null }> {
      if (!this.activeSimilarities) return []

      const dimensions = [
        { key: 'distance', name: 'Distance', value: this.activeSimilarities.distance },
        { key: 'number_units', name: 'Units', value: this.activeSimilarities.number_units },
        { key: 'vintage', name: 'Vintage', value: this.activeSimilarities.vintage },
        { key: 'number_stories', name: 'Stories', value: this.activeSimilarities.number_stories },
        { key: 'quality', name: 'Quality', value: this.activeSimilarities.quality },
        {
          key: 'building_amenities',
          name: 'Building Amenities',
          value: this.activeSimilarities.building_amenities
        },
        {
          key: 'unit_amenities',
          name: 'Unit Amenities',
          value: this.activeSimilarities.unit_amenities
        },
        { key: 'market', name: 'Market', value: this.activeSimilarities.market }
      ]

      // Filter out null values and return top dimensions
      return dimensions.filter((d) => d.value !== null).slice(0, 8)
    }
  },
  methods: {
    formatPercent,
    propertyThumbnail(prop: PropertyType) {
      if ('street_view_url' in prop && prop.street_view_url) {
        return prop.street_view_url
      }
      return getPropertyImageUrl(
        prop as PropertyDetailsResponse,
        import.meta.env.VITE_API_URL,
        useUserStore().isHelloDataEmployee
      )
    },
    safeMiles(value?: number | null): string {
      if (typeof value === 'number') return value.toFixed(2)
      return '-'
    },
    getSimilarityBarColor(value: number | null): string {
      if (value === null) return '#e0e0e0' // gray for null values

      const percentage = value * 100

      if (percentage >= 70) {
        return '#4CAF50' // green
      } else if (percentage >= 40) {
        return '#FFC107' // yellow/amber
      } else {
        return '#F44336' // red
      }
    }
  }
})
</script>

<style scoped>
.metric-label {
  font-size: 0.8em;
  font-weight: 400;
  color: #6c757d;
}

.similarity-breakdown {
  min-width: 120px;
  max-width: 150px;
}

.similarity-dimensions {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.similarity-dimension {
  display: flex;
  flex-direction: column;
}

.dimension-bar-container {
  position: relative;
  background-color: #f0f0f0;
  border-radius: 3px;
  height: 10px;
  overflow: hidden;
}

.dimension-bar {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
  min-width: 1px;
  cursor: pointer;
}

.close-button-container {
  margin-left: auto;
  padding-left: 12px;
}

/* Responsive behavior */
@media (max-width: 1200px) {
  .similarity-breakdown,
  .similarity-separator {
    display: none;
  }
}
</style>
